// The WebGL map (SPEC §15: "WebGL owns the map").
//
// The whole board is ONE texture. 320x320 is 102,400 pixels — less GPU memory
// than a single 512x512 photograph — so there is no tiling, no streaming and no
// level-of-detail pyramid to keep in step. A delta writes a handful of texels
// with texSubImage2D; a keyframe writes the whole thing; panning and zooming
// write nothing at all, because they are two uniforms.
//
// The texture is RGBA16UI carrying country id, HP and flags as INTEGERS, and the
// palette lookup happens in the fragment shader. Uploading colours instead would
// double the upload and make a palette change a full board re-pack, and — worse —
// would need a filterable format, which is the road to a pixel game whose pixels
// are bilinear-filtered. Integer textures cannot be filtered at all, and
// `texelFetch` takes integer texel coordinates, so nearest-neighbour here is a
// property of the format rather than a sampler parameter somebody can change.
//
// This class holds every GL call in the client bar one: the §15 arc pass owns its
// own programs and buffers in arcs.ts, because it is a second instanced draw with
// its own vertex layout and folding it in here would only make this file longer.
// Everything this class decides — which board pixel a click lands on, which
// rectangles to upload, which colour an outcome is — is computed in camera.ts,
// texture.ts, palette.ts, overlay.ts and arcs.ts, which are pure and tested. What
// is left here is binding and drawing.

import { BoardH, BoardW } from '../wire/core';
import type { BoardReplica } from '../net/board';
import type { OptimisticClick } from '../net/clicks';
import { Camera } from './camera';
import { Palette } from './palette';
import { MAP_FRAG, MAP_VERT, OVERLAY_FRAG, OVERLAY_VERT } from './shaders';
import {
  OVERLAY_COLOURS,
  OVERLAY_STRIDE_FLOATS,
  SlotCount,
  packOverlay,
} from './overlay';
import { TexelStride, makeMirror, packAll, packRect, planUploads } from './texture';
import { ArcField, ArcLayer } from './arcs';
import { hexToRGB } from './palette';
import tokens from '../tokens.json';

export interface MapRendererOptions {
  camera?: Camera;
  palette?: Palette;
  /** Render hints, not game rules: the client never applies these, it only draws with them. */
  baseHP?: number;
  fortressHPMultiplier?: number;
  /** Upload calls per frame before the dirty rectangles are merged into their bounding box. */
  maxUploadCalls?: number;
}

export class MapRenderer {
  readonly gl: WebGL2RenderingContext;
  readonly camera: Camera;
  readonly palette: Palette;

  private readonly mirror = makeMirror();
  private readonly boardTex: WebGLTexture;
  private readonly paletteTex: WebGLTexture;
  private paletteRev = -1;

  private readonly mapProgram: WebGLProgram;
  private readonly mapUniforms: Record<string, WebGLUniformLocation | null> = {};
  private readonly mapVAO: WebGLVertexArrayObject;

  private readonly overlayProgram: WebGLProgram;
  private readonly overlayUniforms: Record<string, WebGLUniformLocation | null> = {};
  private readonly overlayVAO: WebGLVertexArrayObject;
  private readonly overlayInstances: WebGLBuffer;
  private overlayScratch = new Float32Array(64 * OVERLAY_STRIDE_FLOATS);

  private readonly baseHP: number;
  private readonly fortressHP: number;
  private readonly maxUploadCalls: number;

  /** The pixel under the cursor, or -1. §15: the hovered pixel always names its owner. */
  hoverPixel = -1;

  /**
   * The §15 attack arcs, drawn after the pixel layer and under the HUD. Null
   * until the app hands one over, so a renderer with no network attached still
   * draws the map.
   */
  arcs: ArcField | null = null;
  /** §15's non-negotiable: keep the arcs, drop the tracer animation. */
  reducedMotion = false;
  private arcLayer: ArcLayer | null = null;

  constructor(canvas: HTMLCanvasElement, opts: MapRendererOptions = {}) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      // The share card of §10 is the same render, captured, so the drawing buffer
      // has to survive until something asks for it.
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });
    if (!gl) throw new Error('render: WebGL2 is required for the map layer');
    this.gl = gl;
    this.camera = opts.camera ?? new Camera();
    this.palette = opts.palette ?? new Palette();
    this.baseHP = opts.baseHP ?? 10;
    this.fortressHP = this.baseHP * (opts.fortressHPMultiplier ?? 50);
    this.maxUploadCalls = opts.maxUploadCalls ?? 8;

    this.boardTex = this.makeBoardTexture();
    this.paletteTex = this.makePaletteTexture();

    this.mapProgram = link(gl, MAP_VERT, MAP_FRAG);
    for (const n of [
      'uBoard',
      'uPalette',
      'uViewport',
      'uOrigin',
      'uScale',
      'uBoardSize',
      'uBackground',
      'uGridColour',
      'uBaseHP',
      'uFortressHP',
      'uHoverPixel',
      'uHoverColour',
      'uValid',
    ]) {
      this.mapUniforms[n] = gl.getUniformLocation(this.mapProgram, n);
    }
    // The full-viewport triangle needs no attributes, but WebGL2 still requires a
    // bound VAO to draw.
    this.mapVAO = must(gl.createVertexArray(), 'vertex array');

    this.overlayProgram = link(gl, OVERLAY_VERT, OVERLAY_FRAG);
    for (const n of ['uViewport', 'uOrigin', 'uScale', 'uColours']) {
      this.overlayUniforms[n] = gl.getUniformLocation(this.overlayProgram, n);
    }
    const { vao, instances } = this.makeOverlayGeometry();
    this.overlayVAO = vao;
    this.overlayInstances = instances;

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
  }

  /** Sizes the drawing buffer to the element, in device pixels. */
  resize(cssWidth: number, cssHeight: number, dpr = 1): void {
    const w = Math.max(1, Math.round(cssWidth * dpr));
    const h = Math.max(1, Math.round(cssHeight * dpr));
    const canvas = this.gl.canvas as HTMLCanvasElement;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    this.camera.resize(w, h);
  }

  /**
   * Pushes whatever the replica has marked dirty into the texture.
   *
   * Call it once per frame, before draw. It is separate from draw so a caller can
   * upload on the network's schedule and draw on the display's, which is what
   * keeps a 2 Hz tick from turning into a 2 Hz frame rate.
   */
  upload(board: BoardReplica): void {
    if (!board.hasDirty()) return;
    const rects = planUploads(board.consumeDirty(), this.maxUploadCalls);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.boardTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);
    for (const r of rects) {
      const w = r.x1 - r.x0;
      const h = r.y1 - r.y0;
      if (w <= 0 || h <= 0) continue;
      if (w === BoardW && h === BoardH) {
        packAll(this.mirror, board.country, board.hp, board.flags);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, BoardW, BoardH, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, this.mirror);
        continue;
      }
      packRect(this.mirror, board.country, board.hp, board.flags, r);
      // Read the sub-rectangle straight out of the full-board mirror. Without the
      // row length the driver would expect w texels per row and the upload would
      // shear.
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, BoardW);
      gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, r.x0);
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, r.y0);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, r.x0, r.y0, w, h, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, this.mirror);
      gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
      gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
      gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
    }
  }

  /** Draws the map and the optimistic overlay. */
  draw(board: BoardReplica, clicks: readonly OptimisticClick[] = [], now = performance.now()): void {
    const gl = this.gl;
    const { width, height } = this.camera.viewport;
    gl.viewport(0, 0, width, height);

    const bg = hexToRGB(tokens.color.base);
    gl.clearColor(bg.r / 255, bg.g / 255, bg.b / 255, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    this.syncPalette();

    gl.useProgram(this.mapProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.boardTex);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);

    const u = this.mapUniforms;
    gl.uniform1i(u.uBoard!, 0);
    gl.uniform1i(u.uPalette!, 1);
    gl.uniform2f(u.uViewport!, width, height);
    gl.uniform2f(u.uOrigin!, this.camera.originX, this.camera.originY);
    gl.uniform1f(u.uScale!, this.camera.scale);
    gl.uniform2f(u.uBoardSize!, BoardW, BoardH);
    setColour(gl, u.uBackground!, tokens.color.base);
    setColour(gl, u.uGridColour!, tokens.color.line);
    gl.uniform1f(u.uBaseHP!, this.baseHP);
    gl.uniform1f(u.uFortressHP!, this.fortressHP);
    gl.uniform1f(u.uHoverPixel!, this.hoverPixel);
    setColour(gl, u.uHoverColour!, tokens.color.cyan);
    gl.uniform1f(u.uValid!, board.valid ? 1 : 0);

    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.mapVAO);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    if (this.arcs && (this.arcs.count() > 0 || this.arcs.glowCount() > 0)) {
      this.arcLayer ??= new ArcLayer(gl);
      this.arcLayer.draw(this.arcs, this.camera, now, this.reducedMotion);
    }

    this.drawOverlay(clicks, now);
    gl.bindVertexArray(null);
  }

  private drawOverlay(clicks: readonly OptimisticClick[], now: number): void {
    const packed = packOverlay(clicks, now, this.overlayScratch);
    this.overlayScratch = packed.data;
    if (packed.count === 0) return;

    const gl = this.gl;
    const { width, height } = this.camera.viewport;
    gl.useProgram(this.overlayProgram);
    const u = this.overlayUniforms;
    gl.uniform2f(u.uViewport!, width, height);
    gl.uniform2f(u.uOrigin!, this.camera.originX, this.camera.originY);
    gl.uniform1f(u.uScale!, this.camera.scale);
    gl.uniform3fv(u.uColours!, this.overlayColourArray());

    gl.bindVertexArray(this.overlayVAO);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.overlayInstances);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      packed.data.subarray(0, packed.count * OVERLAY_STRIDE_FLOATS),
      gl.DYNAMIC_DRAW,
    );

    // Premultiplied source-over: the fragment shader outputs colour*alpha, so
    // overlapping markers on a contested front build up rather than the topmost
    // one occluding the rest — the same stacking §15 wants from the arc layer,
    // without the blown-out whites a straight additive blend gives at high
    // marker density.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, packed.count);
    gl.disable(gl.BLEND);
  }

  private colourArray: Float32Array | null = null;

  private overlayColourArray(): Float32Array {
    if (this.colourArray) return this.colourArray;
    const a = new Float32Array(SlotCount * 3);
    for (let i = 0; i < SlotCount; i++) {
      const c = hexToRGB(OVERLAY_COLOURS[i] ?? '#ffffff');
      a[i * 3] = c.r / 255;
      a[i * 3 + 1] = c.g / 255;
      a[i * 3 + 2] = c.b / 255;
    }
    this.colourArray = a;
    return a;
  }

  private syncPalette(): void {
    if (this.paletteRev === this.palette.revision()) return;
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 256, 256, gl.RGBA, gl.UNSIGNED_BYTE, this.palette.texels);
    this.paletteRev = this.palette.revision();
  }

  private makeBoardTexture(): WebGLTexture {
    const gl = this.gl;
    const tex = must(gl.createTexture(), 'texture');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA16UI, BoardW, BoardH);
    // Integer textures cannot be filtered, and NEAREST is the only legal setting.
    // Stated explicitly anyway: this is the line that says a pixel game must not
    // interpolate its pixels, and the next person reading it should see it.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, BoardW, BoardH, gl.RGBA_INTEGER, gl.UNSIGNED_SHORT, this.mirror);
    return tex;
  }

  private makePaletteTexture(): WebGLTexture {
    const gl = this.gl;
    const tex = must(gl.createTexture(), 'texture');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, 256, 256);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  private makeOverlayGeometry(): { vao: WebGLVertexArrayObject; instances: WebGLBuffer } {
    const gl = this.gl;
    const vao = must(gl.createVertexArray(), 'vertex array');
    gl.bindVertexArray(vao);

    const corners = must(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, corners);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const instances = must(gl.createBuffer(), 'buffer');
    gl.bindBuffer(gl.ARRAY_BUFFER, instances);
    const stride = OVERLAY_STRIDE_FLOATS * 4;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, stride, 8);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, stride, 12);
    gl.vertexAttribDivisor(3, 1);

    gl.bindVertexArray(null);
    return { vao, instances };
  }

  /** Releases every GL object. */
  dispose(): void {
    const gl = this.gl;
    gl.deleteTexture(this.boardTex);
    gl.deleteTexture(this.paletteTex);
    gl.deleteProgram(this.mapProgram);
    gl.deleteProgram(this.overlayProgram);
    gl.deleteVertexArray(this.mapVAO);
    gl.deleteVertexArray(this.overlayVAO);
    gl.deleteBuffer(this.overlayInstances);
    this.arcLayer?.dispose();
    this.arcLayer = null;
  }
}

function must<T>(v: T | null, what: string): T {
  if (v === null) throw new Error(`render: could not create a ${what}`);
  return v;
}

function setColour(gl: WebGL2RenderingContext, loc: WebGLUniformLocation, hex: string): void {
  const c = hexToRGB(hex);
  gl.uniform3f(loc, c.r / 255, c.g / 255, c.b / 255);
}

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = must(gl.createShader(type), 'shader');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new Error(`render: shader compile failed: ${log}`);
  }
  return sh;
}

function link(gl: WebGL2RenderingContext, vert: string, frag: string): WebGLProgram {
  const v = compile(gl, gl.VERTEX_SHADER, vert);
  const f = compile(gl, gl.FRAGMENT_SHADER, frag);
  const p = must(gl.createProgram(), 'program');
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  gl.linkProgram(p);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p);
    gl.deleteProgram(p);
    throw new Error(`render: program link failed: ${log}`);
  }
  return p;
}
